import React, {
  useCallback,
  useContext,
  useState,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowDown, faCircleArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { Button } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import Select from 'react-select';
import { Event, SocketContext } from '../../../../socketProvider';
import camMap from '../../mapLogic';
import { updatePath, updatePaths } from '../../actions';
import {
  inRange,
  socketEventNames,
  customSelectStylesOptions,
  MAX_ZOOM,
} from '../../helper';
import { confirmationDialog, displayErrorModal, displaySuccessModal } from '../../../../helpers/swal';

import style from './style.module.scss';

const {
  ROVER_MOVE,
  ROVER_GET_POSITION,
  ROVER_MOVE_SRS,
  ROVER_COMMAND,
} = socketEventNames;

const CheckSRS = () => {
  const SEPARATOR = '___';
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const connectedStatus = useSelector(({ mapping: { loadingStatus: status } }) => status);
  const socket = useContext(SocketContext);

  const validCombinations = useMemo(
    () => camMap.parseSRSData(SEPARATOR),
    [connectedCamera, connectedStatus],
  );

  const filterSRS = (data, section = null, row = null, seat = null, column) => {
    const uniqueValues = new Set();

    data.forEach((item) => {
      const [s, r, st] = item.split(SEPARATOR);
      if ((section === null || s === section)
          && (row === null || r === row)
          && (seat === null || st === seat)) {
        uniqueValues.add(item);
      }
    });

    const uniqueResults = new Set();

    Array.from(uniqueValues).forEach((value) => {
      const [s, r, st] = value.split(SEPARATOR);
      let columnValue = value;
      if (column === 'section') columnValue = s;
      if (column === 'row') columnValue = r;
      if (column === 'seat') columnValue = st;
      uniqueResults.add(columnValue);
    });

    return Array.from(uniqueResults)
      .sort((a, b) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b, undefined, { numeric: true });
      })
      .map((value) => ({
        value,
        label: value,
      }));
  };

  const handleRandomSelect = () => {
    if (validCombinations.length > 0) {
      const randomIndex = Math.floor(Math.random() * validCombinations.length);
      const [section, row, seat] = validCombinations[randomIndex].split(SEPARATOR);
      setSelectedSection({ value: section, label: section });
      setSelectedRow({ value: row, label: row });
      setSelectedSeat({ value: seat, label: seat });
    }
  };

  const handleGoTo = () => {
    if (!connectedCamera) return;

    try {
      if (!selectedSection || !selectedRow || !selectedSeat) return;

      const command = {
        section: selectedSection.value,
        row: selectedRow.value,
        seat: selectedSeat.value,
        camera: connectedCamera,
      };
      socket.emit(ROVER_MOVE_SRS, 'host', connectedCamera, command);
    } catch (error) {
      console.error('Error sending SRS command:', error);
    }
  };

  return (
    <div className={style.checkSRS}>
      <div className={style.block}>
        <div className={style.top}>
          <span>Check SRS</span>
        </div>
        <div className={style.row}>
          <Select
            value={selectedSection}
            onChange={setSelectedSection}
            options={filterSRS(validCombinations, selectedSection?.value, selectedRow?.value, selectedSeat?.value, 'section')}
            isClearable
            styles={customSelectStylesOptions}
            className={style.select}
            menuPlacement="top"
            placeholder="Section"
          />
          <Button
            variant="outline-primary"
            className="small"
            onClick={handleGoTo}
          >
            Go
          </Button>
        </div>
        <div className={style.row}>
          <Select
            value={selectedRow}
            onChange={setSelectedRow}
            options={filterSRS(validCombinations, selectedSection?.value, selectedRow?.value, selectedSeat?.value, 'row')}
            isClearable
            styles={customSelectStylesOptions}
            className={style.select}
            menuPlacement="top"
            placeholder="Row"
          />
          <Button
            variant="outline-primary"
            className="small"
            onClick={() => {
              setSelectedSection(null);
              setSelectedRow(null);
              setSelectedSeat(null);
            }}
          >
            Clear
          </Button>
        </div>
        <div className={style.row}>
          <Select
            value={selectedSeat}
            onChange={setSelectedSeat}
            options={filterSRS(validCombinations, selectedSection?.value, selectedRow?.value, selectedSeat?.value, 'seat')}
            isClearable
            styles={customSelectStylesOptions}
            className={style.select}
            menuPlacement="top"
            placeholder="Seat"
          />
          <Button
            variant="outline-primary"
            className="small"
            onClick={handleRandomSelect}
          >
            Random
          </Button>
        </div>
      </div>
    </div>
  );
};

const Browse = () => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const update = useCallback((...args) => dispatch(updatePath(...args)), []);
  const pan = useSelector(({ mapping: { browse } }) => browse.reference.pan);
  const tilt = useSelector(({ mapping: { browse } }) => browse.reference.tilt);
  const zoom = useSelector(({ mapping: { browse } }) => browse.reference.zoom);
  const referenceImage = useSelector(({ mapping: { preview: { referenceImage: img } } }) => img);
  const [refImgVariant, setRefImgVariant] = useState('outline-primary');
  const [refPointVariant, setRefPointVariant] = useState('outline-primary');
  const [requestRefPointVariant, setRequestRefPointVariant] = useState('outline-primary');
  const [requestRefImgVariant, setRequestRefImgVariant] = useState('outline-primary');
  const [uploadRefImgVariant, setUploadRefImgVariant] = useState('outline-primary');
  const [refCheckVariant, setRefCheckVariant] = useState('outline-primary');
  const [isRefCheckLoading, setIsRefCheckLoading] = useState(false);

  const fileInputRef = useRef(null);

  const handleRoverCommand = useCallback((slug, camera, arg) => {
    if (!arg?.refCheckResult || camera !== connectedCamera) return;
    setIsRefCheckLoading(false);
    const result = arg.refCheckResult;
    const isOk = result.status === 'ok';
    if (result.error) {
      setRefCheckVariant('danger');
      displayErrorModal({ title: 'Ref Check Failed', text: result.error });
    } else {
      setRefCheckVariant(isOk ? 'success' : 'warning');
      const details = [
        result.status != null && `Status: ${result.status}`,
        result.delta_x != null && `Δx: ${result.delta_x.toFixed(2)}px`,
        result.delta_y != null && `Δy: ${result.delta_y.toFixed(2)}px`,
        result.euclidean_distance != null && `Distance: ${result.euclidean_distance.toFixed(2)}px`,
      ].filter(Boolean);
      const urlPart = result.frame_comparison_url
        ? `<br/><a href="${result.frame_comparison_url}" target="_blank" rel="noopener noreferrer">View comparison image</a>`
        : '';
      const html = details.length
        ? `<p>${details.join('<br/>')}${urlPart}</p>`
        : `<p>Camera position matches reference.${urlPart}</p>`;
      displaySuccessModal({
        title: isOk ? 'Ref Check: Ok' : 'Ref Check: Needs Adjustment',
        html,
        icon: isOk ? 'success' : 'warning',
      });
    }
    setTimeout(() => setRefCheckVariant('outline-primary'), 1000);
  }, [connectedCamera]);

  const runRefCheck = () => {
    if (!connectedCamera) return;
    setIsRefCheckLoading(true);
    socket.emit(ROVER_COMMAND, 'host', connectedCamera, { command: 'ref_check' });
  };

  useEffect(() => {
    if (!connectedCamera) {
      const updates = {
        'browse.reference.pan': '',
        'browse.reference.tilt': '',
        'browse.reference.zoom': '',
        'preview.referenceImage': null,
      };
      dispatch(updatePaths(updates));
    }
  }, [connectedCamera]);

  const requestGetPosition = () => {
    dispatch(updatePath('positionPath', 'browse.reference'));
    socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
  };

  const requestGetReference = () => {
    if (!connectedCamera) return;
    try {
      const camReferencePoint = camMap.getReference();
      update('browse.reference.pan', camReferencePoint.pan);
      update('browse.reference.tilt', camReferencePoint.tilt);
      update('browse.reference.zoom', camReferencePoint.zoom * 1000);
      setRequestRefPointVariant(camReferencePoint ? 'success' : 'danger');
      setTimeout(() => {
        setRequestRefPointVariant('outline-primary');
      }, 1000);
    } catch (error) {
      console.error('Error getting reference point:', error);
    }
  };

  const setReferencePoint = async () => {
    if (!connectedCamera) return;
    if (isEmpty(pan.toString()) || isEmpty(tilt.toString())) return;

    const result = await confirmationDialog({
      title: 'Set Reference PTZ',
      text: 'Are you sure you want to set the reference PTZ?',
    });

    if (result.value) {
      const updateResult = camMap.updateRefPoint(pan, tilt, zoom / 1000);
      setRefPointVariant(updateResult ? 'success' : 'danger');
      setTimeout(() => {
        setRefPointVariant('outline-primary');
      }, 1000);
    }
  };

  const setReferenceImage = async () => {
    if (!connectedCamera) return;

    const result = await confirmationDialog({
      title: 'Set Reference Image',
      text: 'Are you sure you want to set the reference image?',
    });

    if (result.value) {
      try {
        const updateResult = await camMap.updateRefImage();
        setRefImgVariant(updateResult ? 'success' : 'danger');
        setTimeout(() => {
          setRefImgVariant('outline-primary');
        }, 1000);
      } catch (error) {
        console.error('Error updating reference image:', error);
      }
    }
  };

  const sendPositionToCamera = () => {
    const safePan = Number(pan);
    const safeTilt = Number(tilt);
    const safeZoom = Number(zoom / 1000);

    if (connectedCamera && Number.isInteger(safePan)
        && Number.isInteger(safeTilt) && inRange(safeZoom, 0, MAX_ZOOM)) {
      socket.emit(ROVER_MOVE, 'host', connectedCamera, `!${safePan},${safeTilt},${safeZoom}`);
    }
  };

  const toggleReferenceImage = async () => {
    if (!connectedCamera) return;

    if (referenceImage) {
      update('preview.referenceImage', null);
      return;
    }

    try {
      const data = await camMap.getRefImage();
      const blob = new Blob([data], { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(blob);
      update('preview.referenceImage', imageUrl);
    } catch (error) {
      setRequestRefImgVariant('danger');
      setTimeout(() => {
        setRequestRefImgVariant('outline-primary');
      }, 1000);
      console.error('Error getting reference image:', error);
    }
  };

  const handleFileSelect = async (event) => {
    if (!connectedCamera) return;
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      displayErrorModal({ text: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      displayErrorModal({ text: 'File size must be less than 5MB' });
      return;
    }

    const result = await confirmationDialog({
      title: 'Set Reference Image',
      text: 'Are you sure you want to set this as the reference image?',
    });

    if (result.value) {
      try {
        const updateResult = await camMap.updateRefImage(file);
        setUploadRefImgVariant(updateResult ? 'success' : 'danger');
        setTimeout(() => {
          setUploadRefImgVariant('outline-primary');
        }, 1000);
      } catch (error) {
        console.error('Error uploading reference image:', error);
        setUploadRefImgVariant('danger');
        setTimeout(() => {
          setUploadRefImgVariant('outline-primary');
        }, 1000);
      }
    }
  };

  return (
    <div className={style.browseTab}>
      <Event event={ROVER_COMMAND} handler={handleRoverCommand} />
      <div className={style.block}>
        <div className={style.top}>
          <span>Reference Position</span>
          <div>
            <button
              className={style.circleButton}
              onClick={requestGetPosition}
              type="button"
              title="Get position from camera"
            >
              <FontAwesomeIcon icon={faCircleArrowDown} />
            </button>
            <button
              className={style.circleButton}
              onClick={sendPositionToCamera}
              type="button"
              title="Send position to camera"
            >
              <FontAwesomeIcon icon={faCircleArrowUp} />
            </button>
          </div>
        </div>
        <div className={style.row}>
          <span>
            Pan
          </span>
          <input
            className={style.input}
            type="number"
            value={pan}
            onChange={(e) => update('browse.reference.pan', e.target.value)}
          />
          <Button
            variant={requestRefPointVariant}
            className="small"
            onClick={requestGetReference}
          >
            Load Ref
          </Button>
        </div>
        <div className={style.row}>
          <span>
            Tilt
          </span>
          <input
            className={style.input}
            type="number"
            value={tilt}
            onChange={(e) => update('browse.reference.tilt', e.target.value)}
          />
          <Button
            variant={referenceImage ? 'outline-success' : requestRefImgVariant}
            className="small"
            onClick={toggleReferenceImage}
          >
            {referenceImage ? 'Hide Image' : 'View Image'}
          </Button>
        </div>
        <div className={style.row}>
          <span>
            Zoom
          </span>
          <input
            className={style.input}
            type="number"
            value={zoom}
            onChange={(e) => update('browse.reference.zoom', e.target.value)}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileSelect}
          />
          <Button
            variant={uploadRefImgVariant}
            onClick={() => {
              if (!connectedCamera) return;
              fileInputRef.current?.click();
            }}
            className="small"
          >
            Upload Image
          </Button>
        </div>
        <div className={style.row}>
          <Button
            variant={refPointVariant}
            onClick={setReferencePoint}
            className="small"
          >
            Set
          </Button>
          <Button
            variant={refImgVariant}
            onClick={setReferenceImage}
            className="small"
          >
            Set Image
          </Button>
        </div>
      </div>
      <div className={style.block}>
        <div className={style.top}>
          <span>Ref Check</span>
        </div>
        <div className={style.row}>
          <Button
            variant={refCheckVariant}
            className="small"
            onClick={runRefCheck}
            disabled={!connectedCamera || isRefCheckLoading}
          >
            {isRefCheckLoading ? 'Running…' : 'Ref Check'}
          </Button>
        </div>
      </div>
      <CheckSRS />
    </div>
  );
};

export default Browse;
